(ns manage.content-configuration-page.placeholder-expression.db
  (:require [common.datascript.sequence :as dbseq]
            [common.trie :as trie]
            [common.ui.store :as store]
            [common.ui.validation.db
             :refer [transform-entity-validate validation-schema]]
            [datascript.core :as d]
            [lemonpi.content-function.shared.macro :as m]
            [manage.content-configuration-page.context-source.shared :as cs]
            [re-frame.core :as rf]
            [re-posh.core :as rp]))

(def pull-pattern-singular-placeholder-expression
  [:singular-placeholder-expression/strings
   {:singular-placeholder-expression/macro
    [:macro/selector :macro/function {:context-source/_macro cs/pull-pattern-context-source}]}
   :singular-placeholder-expression/starts-with-macro?])

(def pull-pattern-collection-item
  [:collection-item/order
   {:collection-item/override
    (vec (concat pull-pattern-singular-placeholder-expression
                 [{:placeholder/_override [:placeholder/type :placeholder/name :placeholder/visualization-order]}]))}])

(def pull-pattern-placeholder-expression
  (vec (concat pull-pattern-singular-placeholder-expression
               [{:collection-placeholder-expression/item pull-pattern-collection-item}
                {:placeholder/_default-value [:placeholder/type]}
                {:placeholder/_override [:placeholder/type]}])))

(defn- macro-or-string
  "Given a macro expression element, return it either a string, or a valid macro that can be stored in the Datascript database.

  A macro expression element is a valid macro if it is a map containing :alias
  and :selector keywords, and for which a context source with the same alias
  exists in the database.

  If no context source can be found, return the macro as a string. If the macro
  was already a string, return as-is. If the db is not set, just assume the
  context source exists."
  [db e]
  (cond (string? e) e
        (keyword? e) (name e) ; legacy representation, should not be in use
        (m/macro? e)
        (let [dbid [:context-source/alias (name (:alias e))]]
          (if (or (nil? db) (d/entity db dbid))
            (let [{:keys [selector function]} e]
              (cond-> {:context-source/_macro dbid}
                (seq selector) (assoc :macro/selector selector)
                (seq function) (assoc :macro/function function)))
            ;; returns as string and remove the $ so it will not be parsed as
            ;; macro by other processes
            (subs (m/serialize-macro e) 1)))
        :else (str e)))

(defn- conj-or-str
  "Append e to vector v, but if both e and the last element of v are strings,
  concatenate e to the last element of v."
  [v e]
  (if (and (string? (last v)) (string? e))
    (update v (dec (count v)) str e)
    (conj v e)))

(defn singular-placeholder-expression
  [strings macros starts-with-macro?]
  {:singular-placeholder-expression/strings strings
   :singular-placeholder-expression/macro macros
   :singular-placeholder-expression/starts-with-macro? starts-with-macro?})

(defn value->singular-placeholder-expression
  "Parse the user-entered singular placeholder expression value into a list of
   strings and a list of macros with a valid alias."
  [db value]
  (let [p (transduce (map (partial macro-or-string db))
                     conj-or-str
                     [] (m/parse-macro-expr value))]
    (singular-placeholder-expression (filterv string? p) (filterv map? p) (map? (first p)))))

(rp/reg-event-ds :collection-placeholder-expression/copy-default-collection-items
  (fn [db [_ cphe]]
    [{:db/id cphe
      :collection-placeholder-expression/item
      (get-in (d/pull db [{:placeholder/_override
                           [{:placeholder/default-value
                             [{:collection-placeholder-expression/item
                               (assoc-in pull-pattern-collection-item
                                         [1 :collection-item/override 3 :placeholder/_override]
                                         [:db/id])}]}]}]
                      cphe)
              [:placeholder/_override :placeholder/default-value :collection-placeholder-expression/item]
              [])}]))

(rf/reg-sub :collection-placeholder-expression/has-collection-items?
  (fn [[_ cphe]]
    (store/get-value cphe :collection-placeholder-expression/item))
  (fn [first-collection-item _]
    (some? first-collection-item)))

(rf/reg-sub :placeholder-expression/is-override?
  (fn [[_ phe]]
    (store/entity phe '[:placeholder/_override]))
  (fn [placeholder-if-override _]
    (some? (:placeholder/_override placeholder-if-override))))

(rp/reg-event-ds :collection-placeholder-expression/create-collection-item
  (fn [db [_ cphe]]
    (dbseq/db-append
     cphe
     :collection-placeholder-expression/item
     :collection-item/order
     {:collection-item/override
      (for [sp (d/q '[:find [?sp ...] :in $ ?cphe
                      :where
                      (or [?cp :placeholder/default-value ?cphe]
                          [?cp :placeholder/override ?cphe])
                      [?cp :collection-placeholder/structure ?sp]]
                    db cphe)]
        (assoc (value->singular-placeholder-expression db "")
               :placeholder/_override sp))})))

(defn- build-dictionary
  [{:context-source/keys [alias]} structure]
  (letfn [(walk-property [[k v]]
            (case (:type v)
              "object" (map #(str (name k) "." %)
                            (mapcat walk-property (:properties v)))
              "list" (mapcat #(walk-property [(str (name k) "[" % "]") (:items v)])
                             (range 10))
              "tc-string" (map #(str (name k) ":" %) ["vendors(1)"
                                                      "vendors(1, 2)"])
              "integer" (map #(str (name k) ":" %) ["format(\"%,/.1f\")"
                                                    "format(\"%,/.2f\")"])
              [(name k)]))]
    (->> structure
         (mapcat walk-property)
         (map #(str alias "." %)))))

(rf/reg-sub ::macro-dictionary
  :<- [:manage.content-configuration-page.context-source.db/context-sources]
  :<- [:resource.schema/output-structures]
  (fn [[context-sources structure] _]
    (let [words (mapcat (fn [{:context-source/keys [schema] :as context-source}]
                          (let [param (cs/parameter context-source)
                                structure (get-in structure [schema param])]
                            (build-dictionary context-source structure)))
                        context-sources)]
      (trie/build-trie words #{\. \:}))))

(rp/reg-event-ds :collection-placeholder-expression/product-selector->collection-items
  (fn [db [_ cphe pcs num-collection-items mappings]]
    (conj
     ;; retract all existing collection items
     (map #(vector :db/retractEntity (:v %))
          (d/datoms db :eavt cphe :collection-placeholder-expression/item))

     ;; insert new `:collection-placeholder-expression/item`
     {:db/id cphe
      :collection-placeholder-expression/item
      (for [i (range num-collection-items)]
        {:collection-item/order (inc i)
         :collection-item/override
         (for [[sph pr-cs-property] mappings]
           (assoc (singular-placeholder-expression
                   []
                   [{:context-source/_macro pcs :macro/selector [:items i pr-cs-property]}]
                   true)
                  :placeholder/_override sph))})})))

(rp/reg-event-ds ::update-singular
  (fn [db [_ sphe value]]
    [[:db.fn/retractAttribute sphe :singular-placeholder-expression/macro]
     (assoc (value->singular-placeholder-expression db value)
            :db/id sphe)]))

(defn macro [alias selector function]
  (cond-> {:alias (keyword alias)
           :type "m1"}
    (seq selector) (assoc :selector selector)
    (seq function) (assoc :function function)))

(defn macro->cfn-macro
  "Convert datascript pulled representation of a macro to an old-style macro
  representation as used by the content-function library."
  [m]
  (macro (or (get-in m [:context-source/_macro :context-source/alias])
             (get-in m [:context-source/_macro 1]))
         (:macro/selector m)
         (:macro/function m)))

(defn macro-expression
  [{:singular-placeholder-expression/keys [macro strings starts-with-macro?]}]
  (let [macros (map macro->cfn-macro macro)
        pad #(concat % [nil])]
    (remove nil?
            (if starts-with-macro?
              (interleave macros (pad strings))
              (interleave strings (pad macros))))))

(defn value-string
  "Calculate user-visible/user-editable singular placeholder expression value (string)."
  [sphe]
  {:post [string?]}
  (m/serialize-macro-expr (macro-expression sphe)))

(rf/reg-sub :singular-placeholder-expression/value-string
  (fn [[_ phe]]
    (store/entity phe pull-pattern-placeholder-expression))
  (fn [singular-placeholder-expression _]
    (value-string singular-placeholder-expression)))

(rp/reg-query-sub :placeholder-expression/default-ids
  ;; given a content configuration, list all the ids of the placeholder
  ;; expressions that are set as default for a placeholder
  '[:find [?phe ...]
    :in $ ?c
    :where [?c :content-configuration/placeholder ?p]
    [?p :placeholder/default-value ?phe]])

(rp/reg-query-sub :placeholder-expression/placeholder-id
  '[:find ?p .
    :in $ ?phe
    :where
    (or [?p :placeholder/default-value ?phe]
        [?p :placeholder/override ?phe])])

(rp/reg-sub :placeholder-expression/placeholder
  (fn [[_ phe _pattern]]
    (rf/subscribe [:placeholder-expression/placeholder-id phe]))
  (fn [p [_ _phe pattern]]
    {:type :pull
     :pattern pattern
     :id p}))

(rp/reg-query-sub :placeholder-expression/visualization-order
  '[:find ?v .
    :in $ ?phe
    :where
    (or [?p :placeholder/default-value ?phe]
        [?p :placeholder/override ?phe])
    [?p :placeholder/visualization-order ?v]])

(defmethod validation-schema :collection-item
  [_schema value]
  {:validation/schema :collection-placeholder-expression
   :validation/value {:collection-placeholder-expression/item [value]}})

(defmethod transform-entity-validate :singular-placeholder-expression
  [_ sphe]
  (assoc sphe :advertiser/url-whitelist @(rf/subscribe [:resource.advertiser/whitelisted-urls])))

(rp/reg-query-sub :collection-item/identifier-overridden
  '[:find [?ph-name ?rule-name ?item-nr]
    :in $ ?i
    :where
    [?i :collection-item/order ?item-nr]
    [?cphe :collection-placeholder-expression/item ?i]
    [?p :placeholder/override ?cphe]
    [?p :placeholder/name ?ph-name]
    [?r :rule/assignment ?cphe]
    [?r :rule/name ?rule-name]])

(rp/reg-query-sub :collection-item/identifier-default
  '[:find [?ph-name ?item-nr]
    :in $ ?i
    :where
    [?i :collection-item/order ?item-nr]
    [?cphe :collection-placeholder-expression/item ?i]
    [?p :placeholder/default-value ?cphe]
    [?p :placeholder/name ?ph-name]])

(rf/reg-sub :collection-item/query-params
  (fn [[_ {item :eid}]]
    [(rf/subscribe [:collection-item/identifier-default item])
     (rf/subscribe [:collection-item/identifier-overridden item])])
  (fn [[[placeholder-name-default item-nr-default] [placeholder-name-override rule-name item-nr-override]] _]
    (cond->
      {:entity :collection-item
       :item-nr item-nr-default
       :placeholder placeholder-name-default}
      (some? rule-name)
      (assoc :rule rule-name :placeholder placeholder-name-override :item-nr item-nr-override))))

(rp/reg-query-sub :collection-item/find-by-index
  '[:find ?i .
    :in $ ?cphe ?item-nr
    :where
    [?i :collection-item/order ?item-nr]
    [?cphe :collection-placeholder-expression/item ?i]])
